import { Device } from '@capacitor/device';
import { yupResolver } from '@hookform/resolvers/yup';
import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import viewOff from '../../assets/images/view-off-icon.svg';
import viewOn from '../../assets/images/view-on-icon.svg';
import Checkbox from '../../components/layouts/CheckBox';
import Button from '../../components/ui/Button';
import CustomInput from '../../components/ui/CustomInput';
import { ApiConstants } from '../../constants/ApiConstants';
import { ApiTypes, StorageConstants } from '../../constants/Constants';
import { Routes } from '../../constants/RouteConstants';
import authContext from '../../context/authContext';
import useMakeRequest from '../../utils/ApiHelper';
import { deleteFromStorage, getFromStorage, saveToStorage } from '../../utils/Helper';
import LoginValidation from "../../validations/auth/LoginValidation";

interface FormInputs {
  email: string;
  password: string;
  remember_me: boolean;
}

const Login = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const myEmail = queryParams.get("email");

  const navigate = useNavigate();
  const profile: any = useContext(authContext);
  const login: any = useMakeRequest();
  const [showPwd, setShowPwd] = useState(false)
  const [loading, setLoading] = useState(false)
  const { register, watch, setValue, formState: { errors }, handleSubmit } = useForm<FormInputs>({
    mode: 'onChange',
    resolver: yupResolver(LoginValidation),
  });

  useEffect(() => {
    if (myEmail) {
      setValue('email', myEmail);
    }
  }, [myEmail])

  const onSubmit = async (form: FormInputs) => {
    const fcm_token = getFromStorage({ key: StorageConstants?.FCM_TOKEN })
    console.log("login fcm_token: ", fcm_token)
    setLoading(true)
    const vendor_uuid = await Device.getId()
    const info = await Device.getInfo()
    const payload = {
      ...form,
      vendor_uuid: vendor_uuid?.identifier,
      device_name: info?.name,
      model: info?.model,
      platform: info?.platform,
      device_version: info?.osVersion,
      fcm_token: fcm_token,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }

    toast.promise(
      login.makeRequest({ payload, url: ApiConstants?.Login }),
      {
        loading: 'Login...',
        success: (data: any) => data.message,
        error: (error: any) => error.message,
      }
    ).then(() => {
      setLoading(false)
    }).catch(() => {
      setLoading(false)
    })
  }

  useEffect(() => {
    if (login?.status) {
      if (watch('remember_me')) {
        const payload = {
          email: watch('email'),
          password: watch('password'),
        }
        saveToStorage({ key: StorageConstants?.USER_SESSION, value: payload })
      } else {
        deleteFromStorage({ key: StorageConstants?.USER_SESSION })
      }

      console.log("login?.data: ", login?.data);


      if (login?.data?.link_data?.isHouseholdExists) {
        setTimeout(() => {
          const queryParams = new URLSearchParams(login?.data?.link_data.link);
          const user = queryParams.get("user");

          const query = new URLSearchParams({
            user: login?.data?.link_data?.access_type === "temp" ? "1" : "2",
            hhName: login?.data?.link_data?.hhName,
            hh_id: login?.data?.link_data?.hh_id,
            user_id: login?.data?.link_data?.user_id
          }).toString();
          navigate(`/household-invitation?${query}`, { state: { access_token: login?.data?.access_token, userData: login?.data?.data } });
        }, 2000)
      } else {
        if (!login?.data?.is_should_signup && login?.data?.access_token) {
          saveToStorage({ key: StorageConstants?.ACCESS_TOKEN, value: login?.data?.access_token })
          saveToStorage({ key: StorageConstants?.USER_DETAIL, value: login?.data?.data })
          profile.makeRequest({ url: ApiConstants?.Profile, type: ApiTypes?.GET })
        }

        if (login?.data?.data?.is_should_signup) {
          setTimeout(() => {
            navigate(Routes?.SignUp)
          }, 3000);
          return
        } else if (login?.data?.data?.is_profile_setup == 0) {
          navigate(Routes?.ManageProfile)
        } else if (login?.data?.data?.is_profile_setup == 1) {
          setTimeout(() => {
            navigate(Routes?.Dashboard)
          }, 1200);
        }
      }
      login.reset()
    }
  }, [login?.status])

  useEffect(() => {
    const accessToken = getFromStorage({ key: StorageConstants?.ACCESS_TOKEN })
    if (accessToken) {
      login.reset();
      navigate(Routes?.Dashboard)
    }
    const userSession = getFromStorage({ key: StorageConstants?.USER_SESSION })
    if (userSession) {
      setValue('email', myEmail ? myEmail : userSession?.email)
      setValue('password', userSession?.password)
      setValue('remember_me', true)
    }
    return () => {
      login.reset()
    }
  }, [])

  useEffect(() => {
    const isLogined = getFromStorage({ key: StorageConstants?.ACCESS_TOKEN })
    if (isLogined) {
      navigate(Routes?.Dashboard)
    }
  }, [])

  return (
    <main id="main-content" className="auth-page login-page">
      <div className="auth-container">
        <h1 className='auth-title'>Sign In</h1>

        <form onSubmit={handleSubmit(onSubmit)} className={`auth-form-wrap ${loading ? 'disabled-ct' : ''}`}>
          <div className="auth-form">
            <div className="auth-form-fields">
              <CustomInput
                label="Email"
                type="email"
                placeholder="Enter email"
                required
                disabled={myEmail ? true : false}
                {...register('email')}
                error={errors.email?.message}
              />

              <CustomInput
                label="Password"
                type={showPwd ? 'text' : 'password'}
                placeholder="**********"
                required
                {...register('password')}
                error={errors.password?.message}
                icon={
                  showPwd ? <img className="calIcon" src={viewOn} alt="" /> : <img className="calIcon" src={viewOff} alt="" />
                }
                onIconClick={() => setShowPwd(!showPwd)}
              />

              <div className='form-foot'>
                <Checkbox
                  register={register}
                  labelText=""
                  name='remember_me'
                  links={[
                    { text: 'Remember me', isLink: false }
                  ]}
                />
                <Link to={Routes?.ForgotPassword} className='forgot-link'>Forgot Password?</Link>
              </div>
            </div>

            <div className="auth-form-actions">
              <Button
                className='com-btn'
                type='submit'
                loading={loading}
              >
                Sign In
              </Button>

              <div className='auth-footer-text'>
                Don't have an account? <Link to="/signup" className='auth-link'>Sign Up</Link>
              </div>
            </div>
          </div>
        </form>
      </div>
    </main>
  )
}

export default Login